import React from 'react';
import { Modal, Button, Row, Input, Radio, Select } from 'antd';
import PropTypes from 'prop-types';

import { withTranslation } from 'react-i18next';
import ButtonClose from '../common/UI/ButtonClose';
import { VIEW_MODES, VIEW_MODE_ICONS, VIEW_MODE_COLORS } from '../../configs/viewSettings';

import styles from './viewInputModal.less';
import { validateApiKey } from '../../utils/validateApiKey';
import { resolveViewType, VIEW_TYPES } from '../../utils/viewType';

const RadioGroup = Radio.Group;

const VIEW_MODE_ORDER = [
  VIEW_MODES.TABLE,
  VIEW_MODES.CARDS,
  VIEW_MODES.CALENDAR,
  VIEW_MODES.REPORTS,
  VIEW_MODES.PIVOT,
  VIEW_MODES.COUNT,
  VIEW_MODES.HISTORY,
];

class ViewInputModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.inputRef = React.createRef();
    this.state = {
      name: this.props.name,
      originName: this.props.originName,
      type: resolveViewType({ type: this.props.type, forRights: this.props.rights }),
      viewMode: this.props.viewMode || VIEW_MODES.TABLE,
      viewId: this.props.viewId || '',
      viewIdError: '',
    };
  }

  componentDidMount() {
    setTimeout(() => this.inputRef.current && this.inputRef.current.focus(), 0);
  }

  onChange = (e) => this.setState({ [e.target.name]: e.target.value });

  isValid() {
    if (this.state.viewIdError) return false;

    const isForRights = this.state.type === VIEW_TYPES.SHARED;

    if (isForRights) {
      return !!this.state.originName;
    }
    return !!this.state.name;
  }

  onSave = (e) => {
    e.preventDefault();
    if (!this.isValid()) {
      return;
    }

    const isForRights = this.state.type === VIEW_TYPES.SHARED;
    const normalizedName = (this.state.name || this.state.originName || '').trim();
    const normalizedOriginName = (this.state.originName || this.state.name || '').trim();

    this.props.onOk({
      name: normalizedName,
      originName: isForRights ? normalizedOriginName : normalizedName,
      type: this.state.type,
      forRights: this.state.type === VIEW_TYPES.SHARED,
      viewMode: this.state.viewMode,
      viewId: this.state.viewId,
    });
  };

  onSelectType = (e) => {
    this.setState({ type: e.target.value });
  };

  onSelectViewMode = (value) => {
    this.setState({ viewMode: value });
  };

  onChangeViewId = (e) => {
    const value = e.target?.value;
    const originalViewId = this.props.viewId ? String(this.props.viewId) : '';
    const isSameAsOriginal = String(value || '') === originalViewId;
    const error = isSameAsOriginal ? '' : validateApiKey(value, this.props.t);
    this.setState({ viewId: value, viewIdError: error });
  };

  render() {
    let saveButton = null;
    let modalInput = null;
    const { t, isNew } = this.props;

    if (this.state.type === VIEW_TYPES.SHARED) {
      const rightsJustEnabled = isNew || this.props.type !== VIEW_TYPES.SHARED;
      if (rightsJustEnabled) {
        saveButton = isNew ? t('views.modal.createViewAndSaveAccess') : t('views.modal.saveViewAndSaveAccess');
      } else {
        saveButton = t('buttons.save');
      }

      modalInput = (
        <div className={styles.labelsWrapper}>
          <label className={styles.label}>
            <span className={styles.labelText}>{t('rights.originName')}</span>
            &nbsp;
            <span className={styles.requiredFieldAsterisk}>*</span>
            <Input value={this.state.originName} name="originName" required="required" onChange={this.onChange} />
          </label>
          <label className={styles.label}>
            <span className={styles.labelText}>
              {t('rights.name')} {t('views.modal.labelNameHint')}
            </span>
            <Input value={this.state.name} name="name" onChange={this.onChange} />
          </label>
        </div>
      );
    } else {
      if (isNew) {
        saveButton = t('buttons.create');
      } else {
        saveButton = t('buttons.save');
      }

      modalInput = (
        <div className={styles.labelsWrapper}>
          <label className={styles.label}>
            <span className={styles.labelText}>{t('views.modal.inputPlaceholder')}</span>
            &nbsp;
            <span className={styles.requiredFieldAsterisk}>*</span>
            <Input name="name" required value={this.state.name} ref={this.inputRef} onChange={this.onChange} />
          </label>
        </div>
      );
    }

    return (
      <Modal
        open
        className={styles.modal}
        maskClosable={false}
        closable={false}
        width="50%"
        footer={[
          <Button key="submit" type="primary" disabled={!this.isValid()} onClick={this.onSave}>
            {saveButton}
          </Button>,
          <Button key="back" type="default" onClick={this.props.onCancel}>
            {t('buttons.cancel')}
          </Button>,
        ]}
      >
        <Row type="flex" justify="space-between" align="top" className={styles.modalHeader}>
          <div>
            <h2>
              {this.props.headerText}
              <br />
            </h2>
            <span className={styles.headerSubTitle}>{t('views.modal.headerTextSub')}</span>
          </div>
          <ButtonClose large shiftRight onClick={this.props.onCancel} />
        </Row>
        <div className={styles.modalBody}>
          <form onSubmit={this.onSave}>
            {this.props.disabledChangeType ? null : (
              <div>
                <span className={styles.labelText}>{t('views.modal.labelForType')}</span>
                <br />
                <RadioGroup onChange={this.onSelectType} value={this.state.type}>
                  <Radio className={styles.radioButton} value={VIEW_TYPES.PERSONAL}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('views.modal.labelForPrivateView'),
                      }}
                    />
                  </Radio>
                  <Radio className={styles.radioButton} value={VIEW_TYPES.GLOBAL}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('views.modal.labelForGlobalView'),
                      }}
                    />
                  </Radio>
                  <Radio className={styles.radioButton} value={VIEW_TYPES.SHARED}>
                    <span
                      dangerouslySetInnerHTML={{
                        __html: t('views.modal.labelForRigthsView'),
                      }}
                    />
                  </Radio>
                </RadioGroup>
              </div>
            )}

            {modalInput}

            <div className={styles.labelsWrapper}>
              <label className={styles.label}>
                <span className={styles.labelText}>{t('views.modal.labelForViewMode')}</span>
                <br />
                <Select
                  value={this.state.viewMode}
                  onChange={this.onSelectViewMode}
                  style={{ width: 220 }}
                  optionLabelProp="label"
                  className={styles.viewModeSelect}
                >
                  {VIEW_MODE_ORDER.map((mode) => {
                    const Icon = VIEW_MODE_ICONS[mode];
                    const color = VIEW_MODE_COLORS[mode];
                    const label = t(`views.modal.viewModes.${mode}`);
                    return (
                      <Select.Option
                        key={mode}
                        value={mode}
                        label={
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            <Icon style={{ color, marginRight: 7 }} />
                            {label}
                          </span>
                        }
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <Icon style={{ color, marginRight: 7 }} />
                          {label}
                        </span>
                      </Select.Option>
                    );
                  })}
                </Select>
              </label>
            </div>
            {!isNew && (
              <div className={styles.labelsWrapper}>
                <label className={styles.label}>
                  <span className={styles.labelText}>{t('modals.apiKeys.label')}</span>
                  <br />
                  <Input
                    value={this.state.viewId}
                    style={{ width: 220 }}
                    onChange={this.onChangeViewId}
                  />
                  {this.state.idError ? (
                    <span className={styles.error}>{t('modals.apiKeys.promptHint')}</span>
                  ) : (
                    <span className={styles.hint}>{t('modals.apiKeys.hintEmpty')}</span>
                  )}
                  <br /><br />
                </label>
              </div>
            )}
          </form>
        </div>
      </Modal>
    );
  }
}

ViewInputModal.propTypes = {
  headerText: PropTypes.string,
  value: PropTypes.string,
  rights: PropTypes.bool,
  type: PropTypes.string,
  viewMode: PropTypes.string,
  viewId: PropTypes.string,
  onOk: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  okText: PropTypes.string,
  disabledChangeType: PropTypes.bool,
};

export default withTranslation()(ViewInputModal);
